import {
  IAccount,
  IAccountSubIndustry,
  IAccountIndustry,
  IAccountSalesChannel,
  IInvestor,
} from '@common/interfaces/salesforce'
import { combineReducers } from 'redux'
import { IAction } from '../apiMiddleware'
import * as actions from './actions'

interface IAccountDependenciesState {
  industries?: IAccountIndustry[]
  subIndustries?: IAccountSubIndustry[]
  salesChannels?: IAccountSalesChannel[]
}

const initialAccountDependenciesState: IAccountDependenciesState = {
  industries: [] as IAccountIndustry[],
  subIndustries: [] as IAccountSubIndustry[],
  salesChannels: [] as IAccountSalesChannel[],
}

export default combineReducers({
  loadingArray(state: string[] = [], action: IAction): string[] {
    switch (action.type) {
      case actions.LIST_ACCOUNTS_REQUEST:
      case actions.SHOW_ACCOUNT_REQUEST:
      case actions.UPDATE_ACCOUNT_REQUEST:
      case actions.GET_ACCOUNT_TYPE_OPTIONS_REQUEST:
        return [...state, action.type]
      case actions.LIST_ACCOUNTS_SUCCESS:
      case actions.LIST_ACCOUNTS_FAILURE:
      case actions.LIST_INVESTORS_SUCCESS:
      case actions.LIST_INVESTORS_FAILURE:
      case actions.SHOW_ACCOUNT_SUCCESS:
      case actions.SHOW_ACCOUNT_FAILURE:
      case actions.UPDATE_ACCOUNT_SUCCESS:
      case actions.UPDATE_ACCOUNT_FAILURE:
      case actions.GET_ACCOUNT_TYPE_OPTIONS_SUCCESS:
      case actions.GET_ACCOUNT_TYPE_OPTIONS_FAILURE:
        return state.filter(
          (item) =>
            item !== action.type.replace('SUCCESS', 'REQUEST').replace('FAILURE', 'REQUEST'),
        )
      default:
        return state
    }
  },
  accounts(state: IAccount[] = [], action: IAction): IAccount[] {
    switch (action.type) {
      case actions.LIST_ACCOUNTS_SUCCESS:
        return action.data as IAccount[]
      default:
        return state
    }
  },
  account(state: IAccount = null, action: IAction): IAccount {
    switch (action.type) {
      case actions.SHOW_ACCOUNT_SUCCESS:
      case actions.UPDATE_ACCOUNT_SUCCESS:
        return action.data as IAccount
      case actions.HIDE_ACCOUNT:
        return null
      default:
        return state
    }
  },
  accountDependencies(
    state: IAccountDependenciesState = initialAccountDependenciesState,
    action: IAction,
  ): IAccountDependenciesState {
    switch (action.type) {
      case actions.DESCRIBE_ACCOUNTS_SUCCESS:
        return action.data as IAccountDependenciesState
      default:
        return state
    }
  },
  investors(state: IInvestor[] = [], action: IAction): IInvestor[] {
    switch (action.type) {
      case actions.LIST_INVESTORS_SUCCESS:
        return [...(action.data as IInvestor[]), { id: 'Individual', name: 'Individual' }]
      default:
        return state
    }
  },
  accountTypeOptions(
    state: {
      categoriesWithSubs: Array<{
        value: string
        label: string
        subCategories: Array<{ value: string; label: string }>
      }>
      subCategories?: Array<{ value: string; label: string }>
      isCategoryEditable?: boolean
      recordTypeId?: string | null
    } | null = null,
    action: IAction,
  ): {
    categoriesWithSubs: Array<{
      value: string
      label: string
      subCategories: Array<{ value: string; label: string }>
    }>
    subCategories?: Array<{ value: string; label: string }>
    isCategoryEditable?: boolean
    recordTypeId?: string | null
  } | null {
    switch (action.type) {
      case actions.GET_ACCOUNT_TYPE_OPTIONS_SUCCESS: {
        const data = action.data as
          | {
              categoriesWithSubs?: Array<{
                value: string
                label: string
                subCategories: Array<{ value: string; label: string }>
              }>
              subCategories?: Array<{ value: string; label: string }>
              isCategoryEditable?: boolean
              recordTypeId?: string | null
            }
          | undefined
        // Only store categoriesWithSubs if it exists (when called without category parameter)
        if (data?.categoriesWithSubs) {
          return {
            categoriesWithSubs: data.categoriesWithSubs,
            subCategories: data.subCategories,
            isCategoryEditable: data.isCategoryEditable,
            recordTypeId: data.recordTypeId,
          }
        }
        // If categoriesWithSubs is not provided (when called with category parameter),
        // preserve existing categoriesWithSubs but update account-specific fields
        if (state) {
          return {
            ...state,
            isCategoryEditable: data?.isCategoryEditable ?? state.isCategoryEditable,
            recordTypeId: data?.recordTypeId ?? state.recordTypeId,
          }
        }
        return null
      }
      default:
        return state
    }
  },
})
